import { type RefObject, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ProjectionNameEnum, ViewportKind } from "@mm/metx-workbench.meteomatics.com";

import mapboxgl, { type ResourceType, ScaleControl, type Style } from "mapbox-gl";

import "./style.scss";

import {
  MAP_RESIZE_RENDER_FALLBACK_TIMEOUT_MS,
  type MapboxMapWithUpdate,
  applyCanvasResizeCompensation,
  applyElementResizeCompensation,
  clearCanvasResizeCompensation,
  clearElementResizeCompensation,
  getElementSize,
  getRenderedMapSize,
  getResizeCommitDelay,
  isSameSize,
} from "@/map/mapResizeUtils";
import { setLayerPropsChanges, setViewportChanges } from "@/reducer/PrefetchState";
import { observeResizeWithAnimationFrame } from "@/utility/observeResizeWithAnimationFrame";
import { EffectResult, traceComponent } from "@/utility/tracing";

import { ENV } from "@/env";

import { type ScenePublicApi, compositor } from "@/layers/Compositor";
import { shouldUseProjectionFallback } from "@/layers/utility/projectionSupport";
import { usePopupPlot } from "@/reducer/plot/PopupPlotContext";
import { setPopupPlotState } from "@/reducer/plot/reducer";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import Logger from "@mm/logging";

import { SnapshotToolHtmlIds, generateIdForSnapshot } from "@/animation/video-generator";
/**
 * Global map icon configurations. Uses Sprite images.
 * Sprite images: https://docs.mapbox.com/mapbox-gl-js/style-spec/sprite/
 * These icons are used to render icons based on GeoJSONFeatureCollection in layer implementations.
 *
 * CAUTION: We need to keep this, so webpack copies over atlases to media folder,
 * unless we figure out how to configure webpack to copy these files without importing.
 */
import { useCurrentTabViewportLinkingStatus } from "@/grid/useCurrentTabViewportLinkingStatus";
import { buildTabLocationMarkersLayer } from "@/map/location-markers/TabLocationMarkersLayer";
import { createPinMarkerElement } from "@/map/location-markers/pinMarkerElement";
import { useTabLocationMarkers } from "@/map/location-markers/useTabLocationMarkers";
import {
  emitClickToInteractionStateMachine,
  emitContextMenuToInteractionStateMachine,
  fetchIconImg,
} from "@/map/mapboxListeners";
import { useDrawingLayerDesc } from "@/models/map-drawing-tools";
import {
  useCurrMapInteractionState,
  useMapInteractionService,
} from "@/models/map-interaction/mapInteractionStateHooks";
import { useTempLayerDescriptions } from "@/models/temporal-layer/tempLayerStateMachineHooks";
import {
  useCurrTimeState,
  useDisplayTime,
  useNextDisplayTime,
  useTimezone,
} from "@/models/time-control/timeStateHooks";
import { TimeStates } from "@/models/time-control/timeStateTypes";
import { WindowKind, instanceId, useDesktop } from "@/overlay/components/window";
import type { RootState } from "@/reducer";
import { useDescriptionLayers } from "@/reducer/MapsState";
import { useTabLocationMarkersEnabled } from "@/reducer/TabsState";
import { setViewport } from "@/reducer/ViewportsState";
import { batchGroupBy } from "@/reducer/batchGroupBy";
import { useMapAndViewport } from "@/reducer/selectors/ToolsSelectors";
import { SentrySpans } from "@/sentry/SentrySpans";
import type { MapId } from "@/types/type-utils";
import { BUILD_INFO } from "@mm/build-info";
import { useFlag } from "@openfeature/react-sdk";

const logger = Logger.fromFilename(__filename);

/**
 * If enabled, prints debug messages to the console that aid in debugging synchronization problems between
 * the reactive data store of the application and the state within the mapbox map.
 *
 * To enable, replace `false` with `true`.
 *
 * Another tip is to replace `useTracedEffect` calls with `useEffect`. This will print a list of missing
 * dependencies during linting. However, make sure these messages are not false positives!
 */

const __TRACE_MAP_CONSOLIDATION = !BUILD_INFO.isProduction && false;
const MAPBOX_MIN_TILE_CACHE_SIZE = 128;

type SerializedMapId = string;

export function serializeMapId(id: MapId): SerializedMapId {
  return [id.map, id.profile, id.tab].join(".");
}

const glyphs = `https://api.maptiler.com/fonts/{fontstack}/{range}.pbf?key=${ENV.maptilerKey}`;

mapboxgl.accessToken = ENV.mapboxKey;

// This check is needed because of hot-reload error: Uncaught Error: setRTLTextPlugin cannot be called multiple times.
if (process.env.NODE_ENV !== "test" && mapboxgl.getRTLTextPluginStatus() === "unavailable") {
  // Plugin for right-to-left languages. Added for Arabic support.
  // https://docs.mapbox.com/mapbox-gl-js/api/properties/#setrtltextplugin
  mapboxgl.setRTLTextPlugin(
    new URL("mapbox-plugins/mapbox-gl-rtl-text/v0.2.0/mapbox-gl-rtl-text.js", document.location.origin).href,
    (e) => {
      console.log("ERROR:", e);
    },
  );
}

const emptyStyle: Style = {
  version: 8,
  sources: {},
  layers: [],
  glyphs,
};

// TODO: the viewport sync code is soooooooo dump. Reiner see your notes on page 62.

type MapProps = {
  id: MapId;
  resizeCompensationRef?: RefObject<HTMLDivElement>;
};
export { MapImpl as Map };

/**
 * A react component wrapping `Visualization` into a reactive component.
 *
 * @param props
 * @returns
 */
function MapImpl(props: MapProps) {
  const { useTracedEffect, trace } = traceComponent(__TRACE_MAP_CONSOLIDATION, logger, serializeMapId(props.id));

  const [cartographicMap, viewport] = useMapAndViewport(props.id);
  const dispatch = useDispatch();
  const displayTime = useDisplayTime();
  const nextTime = useNextDisplayTime();

  const isLayoutArrangementActive = useSelector((state: RootState) => state.ui.isLayoutArrangementActive);

  const [scene, setScene] = useState<ScenePublicApi | null>(null);
  const mapElement: RefObject<HTMLDivElement> | null = useRef(null);
  const descriptionLayers = useDescriptionLayers(props.id.map);
  const tempDescriptionLayers = useTempLayerDescriptions(props.id.map);
  const drawingLayer = useDrawingLayerDesc(props.id.map);
  const tabLocationMarkersEnabled = useTabLocationMarkersEnabled(props.id.tab);
  const tabLocationMarkers = useTabLocationMarkers(props.id.tab);
  const { state: plotState, dispatch: popupPlotDispatch } = usePopupPlot();
  const { isWindowVisible } = useDesktop();
  const isPopupPlotOpen = isWindowVisible(instanceId(WindowKind.PopupPlot));
  const { coords } = plotState;

  const timeOffsetMinutes = cartographicMap?.time_offset_mins;
  const mapProjection = cartographicMap?.map_projection;
  const mapLodBias = cartographicMap?.lod_bias;

  const mapInteractionState = useCurrMapInteractionState();
  const showCrosshairCursor = mapInteractionState.matches("COORD_SELECT");
  const { mapService } = useMapInteractionService();

  const timeState = useCurrTimeState();
  const isAnimation = timeState.matches(TimeStates.ANIMATION);
  const timezone = useTimezone();

  // Feature flags may resolve asynchronously after first paint; include this in relevant effect deps
  // so layer-stack reconciliation reruns when WMTS toggles.
  const { value: wmtsEnabled } = useFlag("wmts", false);
  // keep object equality stable for downstream effects and observer callbacks
  const mapId = useMemo(
    () => ({
      map: props.id.map,
      profile: props.id.profile,
      tab: props.id.tab,
    }),
    [props.id.map, props.id.profile, props.id.tab],
  );

  const scale = useMemo(
    () =>
      new ScaleControl({
        maxWidth: 100,
        unit: "metric",
      }),
    [],
  );

  const tabLocationMarkersLayer = useMemo(
    () => buildTabLocationMarkersLayer(props.id.tab, tabLocationMarkersEnabled, tabLocationMarkers),
    [props.id.tab, tabLocationMarkersEnabled, tabLocationMarkers],
  );

  // mapbox only observes the window size for automatic resizing.
  // As a result, we have to notify it explicitly when the grid/sidebar changes.
  // Keep the last rendered frame visually compensated during animated layout changes and only commit
  // real resizes sparsely. This avoids a full white flash on every frame while reducing the amount of
  // stretch/squeeze compared to a single end-of-animation commit.
  useEffect(() => {
    const element = mapElement.current;
    if (!element || !scene) {
      return;
    }
    const containerElement: HTMLElement = element;
    const sceneApi: ScenePublicApi = scene;
    const overlayCompensationElement = props.resizeCompensationRef?.current ?? null;

    const map = sceneApi.getMapboxMap();
    let resizeCommitDelayId: number | null = null;
    let resizeFinalizeTimeoutId: number | null = null;
    let resizeCommitInFlight = false;
    let renderedSize = getRenderedMapSize(map);
    let lastObservedSize = renderedSize;
    let lastCommitAt = Number.NEGATIVE_INFINITY;
    let lastNativeWindowResizeAt = Number.NEGATIVE_INFINITY;

    function clearPendingCommit() {
      if (resizeCommitDelayId !== null) {
        window.clearTimeout(resizeCommitDelayId);
        resizeCommitDelayId = null;
      }
    }

    function clearPendingFinalize() {
      map.off("render", finalizeResizeCommit);
      if (resizeFinalizeTimeoutId !== null) {
        window.clearTimeout(resizeFinalizeTimeoutId);
        resizeFinalizeTimeoutId = null;
      }
    }

    function clearResizeCompensation() {
      clearCanvasResizeCompensation(map);
      if (overlayCompensationElement) {
        clearElementResizeCompensation(overlayCompensationElement);
      }
    }

    function applyResizeCompensation(targetSize: ReturnType<typeof getElementSize>) {
      applyCanvasResizeCompensation(map, renderedSize, targetSize);
      if (overlayCompensationElement) {
        applyElementResizeCompensation(overlayCompensationElement, renderedSize, targetSize);
      }
    }

    function finalizeResizeCommit() {
      clearPendingFinalize();
      resizeCommitInFlight = false;
      renderedSize = getRenderedMapSize(map);
      lastObservedSize = renderedSize;
      lastCommitAt = performance.now();

      const nextElementSize = getElementSize(containerElement);
      if (isSameSize(renderedSize, nextElementSize)) {
        clearResizeCompensation();
        return;
      }

      lastObservedSize = nextElementSize;
      applyResizeCompensation(lastObservedSize);
      scheduleResizeCommit();
    }

    function commitResize() {
      clearPendingCommit();

      if (resizeCommitInFlight || isSameSize(renderedSize, lastObservedSize)) {
        return;
      }

      resizeCommitInFlight = true;

      map.once("render", finalizeResizeCommit);
      resizeFinalizeTimeoutId = window.setTimeout(finalizeResizeCommit, MAP_RESIZE_RENDER_FALLBACK_TIMEOUT_MS);

      const mapWithUpdate = map as MapboxMapWithUpdate;
      mapWithUpdate.resize({ originalEvent: new Event("resize") });
      sceneApi.syncToMapboxViewport();
      mapWithUpdate._update?.();
    }

    function scheduleResizeCommit(force = false) {
      if (force) {
        commitResize();
        return;
      }

      if (resizeCommitInFlight || isSameSize(renderedSize, lastObservedSize)) {
        return;
      }

      const pixelDelta = Math.max(
        Math.abs(lastObservedSize.width - renderedSize.width),
        Math.abs(lastObservedSize.height - renderedSize.height),
      );
      const elapsedSinceLastCommit = performance.now() - lastCommitAt;
      const delay = getResizeCommitDelay(pixelDelta, elapsedSinceLastCommit);

      clearPendingCommit();
      resizeCommitDelayId = window.setTimeout(() => {
        resizeCommitDelayId = null;
        commitResize();
      }, delay);
    }

    function handleObservedResize() {
      if (performance.now() - lastNativeWindowResizeAt < 80) {
        renderedSize = getRenderedMapSize(map);
        lastObservedSize = renderedSize;
        clearResizeCompensation();
        clearPendingCommit();
        clearPendingFinalize();
        resizeCommitInFlight = false;
        return;
      }

      const nextSize = getElementSize(containerElement);
      if (isSameSize(nextSize, lastObservedSize)) {
        return;
      }

      lastObservedSize = nextSize;
      applyResizeCompensation(nextSize);
      scheduleResizeCommit();
    }

    function handleWindowResize() {
      lastNativeWindowResizeAt = performance.now();
      clearPendingCommit();
      clearPendingFinalize();
      resizeCommitInFlight = false;
      sceneApi.syncToMapboxViewport();
      renderedSize = getRenderedMapSize(map);
      lastObservedSize = renderedSize;
      clearResizeCompensation();
    }

    function handleSidebarTransitionEnd(event: Event) {
      const transitionEvent = event as TransitionEvent;
      const sidebarElement = document.getElementById("app-sidebar");
      if (transitionEvent.target !== sidebarElement || transitionEvent.propertyName !== "width") {
        return;
      }

      lastObservedSize = getElementSize(containerElement);
      applyResizeCompensation(lastObservedSize);
      scheduleResizeCommit(true);
    }

    const stopObservingResize = observeResizeWithAnimationFrame(containerElement, handleObservedResize);
    const sidebarElement = document.getElementById("app-sidebar");

    window.addEventListener("resize", handleWindowResize);
    sidebarElement?.addEventListener("transitionend", handleSidebarTransitionEnd);

    return () => {
      stopObservingResize();
      window.removeEventListener("resize", handleWindowResize);
      sidebarElement?.removeEventListener("transitionend", handleSidebarTransitionEnd);
      clearPendingCommit();
      clearPendingFinalize();
      clearResizeCompensation();
    };
  }, [props.resizeCompensationRef, scene]);

  useTracedEffect(
    "update cursor to match mapInteractionState",
    () => {
      if (!scene) {
        return EffectResult.EarlyOut;
      }

      const map = scene.getMapboxMap();
      if (showCrosshairCursor) {
        map.getCanvas().style.cursor = "crosshair";
      } else {
        map.getCanvas().style.cursor = "unset";
      }
      return EffectResult.Executed;
    },
    [scene, showCrosshairCursor],
  );

  // update viewport of mapbox to match redux store
  useTracedEffect(
    "update viewport of mapbox to match redux store",
    () => {
      // This place is causing sync bug
      // See hacky solution in TabSettingsWindow/index.tsx line 41
      // TODO Refactor whole syncing to flag inside DB so we have more control on behavior
      //   TODO last updated by is the cause of undo problem, we need to overcome that.
      if (scene && viewport && viewport.lastUpdatedBy !== mapId.map) {
        scene.setViewportDescription(viewport);
        return EffectResult.Executed;
      }
      return EffectResult.EarlyOut;
    },
    [viewport, scene, mapId],
  );

  useTracedEffect(
    "update scene to match viewport of mapbox",
    () => {
      if (!scene) {
        return EffectResult.EarlyOut;
      }
      scene.syncToMapboxViewport();
      return EffectResult.Executed;
    },
    [scene, viewport],
  );

  useTracedEffect(
    "set marker when needed",
    () => {
      //TODO: simplify
      if (!scene) {
        return EffectResult.EarlyOut;
      }
      const map = scene.getMapboxMap();
      const marker = scene.getPopupMarker();
      if (!isPopupPlotOpen) {
        if (marker) {
          marker.remove();
        }
        return EffectResult.EarlyOut;
      }

      if (coords && map) {
        if (marker) {
          marker.remove();
        }
        const tmpMarker = new mapboxgl.Marker({
          element: createPinMarkerElement({ fillColor: "#F6970A", width: 35, height: 35, zIndex: 1 }),
          anchor: "bottom",
          draggable: true,
        })
          .setLngLat([coords.lon, coords.lat])
          .addTo(map);
        scene.setPopupMarker(tmpMarker);
        tmpMarker.on("dragend", onDragEnd.bind(tmpMarker));
      } else {
        marker?.remove();
      }

      return () => {
        marker?.remove();
      };

      function onDragEnd() {
        // @ts-ignore
        if (this) {
          // @ts-ignore
          const lngLat = this.getLngLat();

          popupPlotDispatch(
            setPopupPlotState({
              type: plotState.type,
              coords: { lon: lngLat.lng, lat: lngLat.lat },
            }),
          );
        }
      }
    },
    [scene, isPopupPlotOpen, coords],
  );

  // update redux store to match mapbox viewport
  useTracedEffect(
    "update redux store to match mapbox viewport",
    () => {
      if (!scene) {
        return EffectResult.EarlyOut;
      }

      const map = scene.getMapboxMap();
      const isResizeTriggeredMapEvent = (event: { originalEvent?: Event }) => event.originalEvent?.type === "resize";

      map.on("move", onMapBoundsChange);
      // Move start and move end original events are actual user interactions, and they help starting and stopping batches of viewport changes
      // 'movestart' is executed before 'move', and 'moveend' is executed after 'move'
      map.on("movestart", onMapMoveStart);
      map.on("moveend", onMapMoveEnd);

      const handleMissingImage = fetchIconImg(map);

      map.on("styleimagemissing", handleMissingImage);

      const emitClickToStateMachine = emitClickToInteractionStateMachine(mapId.map, mapService);
      map.on("click", emitClickToStateMachine);

      const emitContextMenuToStateMachine = emitContextMenuToInteractionStateMachine(mapId.map, mapService);
      map.on("contextmenu", emitContextMenuToStateMachine);

      return () => {
        map.off("move", onMapBoundsChange);
        map.off("movestart", onMapMoveStart);
        map.off("moveend", onMapMoveEnd);
        map.off("styleimagemissing", handleMissingImage);
        map.off("click", emitClickToStateMachine);
        map.off("contextmenu", emitContextMenuToStateMachine);
      };

      function onMapMoveStart(event: any) {
        if (!event.originalEvent || isResizeTriggeredMapEvent(event)) {
          return;
        }

        // Stop the on-going map loading speed measurement if the user has interacted with the map,
        // because an interaction causes new content to load and measurements become inconsistent.
        SentrySpans.discardLayerSpans();
        batchGroupBy.start();
      }

      function onMapMoveEnd(event: any) {
        if (!event.originalEvent || isResizeTriggeredMapEvent(event)) {
          return;
        }

        batchGroupBy.end();
      }

      function onMapBoundsChange(event: any) {
        if (!cartographicMap) {
          return;
        }
        // `isUserInteraction` is false if the change was triggered programmatically using the Mapbox API. In this case, do nothing
        // to avoid endless recursion.
        const isUserInteraction = !!event.originalEvent && !isResizeTriggeredMapEvent(event);
        if (map && isUserInteraction) {
          const bounds = map.getBounds();
          if (!bounds) {
            return;
          }

          const sw = bounds.getSouthWest();
          const ne = bounds.getNorthEast();

          const zoom = map.getZoom();
          const center = map.getCenter();
          const projectionName = map.getProjection?.()?.name;
          const isGlobe = projectionName === ProjectionNameEnum.globe;

          dispatch(
            setViewport({
              mapId: mapId.map,
              viewport: {
                id: cartographicMap.id_viewport,
                // if the viewport is changed
                kind: isGlobe ? ViewportKind.ViewportCenterPoint : ViewportKind.ViewportFull,
                southWest_lng: isGlobe ? center.lng : sw.lng,
                southWest_lat: isGlobe ? center.lat : sw.lat,
                northEast_lng: isGlobe ? center.lng : ne.lng,
                northEast_lat: isGlobe ? center.lat : ne.lat,
                zoom,
                center_lat: center.lat,
                center_lng: center.lng,
              },
            }),
          );
          dispatch(
            setViewportChanges({
              id: mapId,
              viewport: {
                // if the viewport is changed
                kind: isGlobe ? ViewportKind.ViewportCenterPoint : ViewportKind.ViewportFull,
                southWest_lng: isGlobe ? center.lng : sw.lng,
                southWest_lat: isGlobe ? center.lat : sw.lat,
                northEast_lng: isGlobe ? center.lng : ne.lng,
                northEast_lat: isGlobe ? center.lat : ne.lat,
                zoom,
                center_lat: center.lat,
                center_lng: center.lng,
              },
            }),
          );
        }
      }
    },
    [scene, dispatch, mapId, cartographicMap?.id_viewport],
  );

  useTracedEffect(
    "update scene to match abstract datetime description",
    () => {
      if (!scene) {
        return EffectResult.EarlyOut;
      }
      scene.setIsAnimation(isAnimation);
      return EffectResult.Executed;
    },
    [scene, isAnimation],
  );

  useTracedEffect(
    "update relOffsetTime scene to match concrete/realized datetime",
    () => {
      if (!scene) {
        return EffectResult.EarlyOut;
      }
      scene.setTimeOffsetMinutes(timeOffsetMinutes);
      return EffectResult.Executed;
    },
    [scene, timeOffsetMinutes],
  );

  useTracedEffect(
    "update mapProjection scene to match concrete/realized mapProjection",
    () => {
      if (!scene || !mapProjection) {
        return EffectResult.EarlyOut;
      }
      if (mapProjection.name !== scene.getProjection().name && !!descriptionLayers) {
        const sortedLayers = descriptionLayers.toSorted((a, b) => {
          if (a.index < b.index) {
            return -1;
          }
          if (a.index > b.index) {
            return 1;
          }
          return 0;
        });

        scene.setLayerStackDescription(
          // Keep drawing in its established scene-stack position. Tab location markers self-order below
          // active Mapbox Draw layers when those layers exist.
          [...sortedLayers, drawingLayer, tabLocationMarkersLayer],
          timezone,
          shouldUseProjectionFallback(mapProjection.name),
          wmtsEnabled,
        );
      }

      scene.setProjection(mapProjection);
      return EffectResult.Executed;
    },
    [scene, timezone, mapProjection, wmtsEnabled, descriptionLayers, drawingLayer, tabLocationMarkersLayer],
  );

  useTracedEffect(
    "update lodBias scene to match concrete/realized lodBias",
    () => {
      if (!scene || mapLodBias === undefined) {
        return EffectResult.EarlyOut;
      }
      scene.setLodBias(mapLodBias);
      return EffectResult.Executed;
    },
    [scene, mapLodBias],
  );

  useTracedEffect(
    "update scene to match concrete/realized datetime",
    () => {
      if (!scene || !displayTime) {
        return EffectResult.EarlyOut;
      }
      scene.setDisplayTime(displayTime);
      return EffectResult.Executed;
    },
    [scene, displayTime],
  );

  useTracedEffect(
    "update scene to match next/pre-cache datetime",
    () => {
      if (!scene || !nextTime) {
        return EffectResult.EarlyOut;
      }
      scene.setNextTime(nextTime);
      return EffectResult.Executed;
    },
    [scene, nextTime],
  );

  // update layer stack
  useTracedEffect(
    "update scene to match layer stack description",
    () => {
      if (!descriptionLayers || !scene) {
        return EffectResult.EarlyOut;
      }
      const _descriptionLayers = [...descriptionLayers];
      const sortedLayers = _descriptionLayers.sort((a, b) => {
        if (a.index < b.index) {
          return -1;
        }
        if (a.index > b.index) {
          return 1;
        }
        return 0;
      });

      scene.setLayerStackDescription(
        // Keep drawing in its established scene-stack position. Tab location markers self-order below
        // active Mapbox Draw layers when those layers exist.
        [...sortedLayers, ...tempDescriptionLayers, drawingLayer, tabLocationMarkersLayer],
        timezone,
        shouldUseProjectionFallback(mapProjection?.name),
        wmtsEnabled,
      );

      dispatch(
        setLayerPropsChanges({
          id: mapId,
          layers: sortedLayers,
        }),
      );

      return EffectResult.Executed;
    },
    [descriptionLayers, tempDescriptionLayers, timezone, scene, wmtsEnabled, drawingLayer, tabLocationMarkersLayer],
  );

  const allAvailable = viewport != null && timeState != null;

  // This effect should be run once after all data becomes available for the first time
  // We check this condition with `allAvailable`. It may happen that react reuses this
  // component for another map, so we have to observe the mapId to reinitialize reuses.
  useTracedEffect(
    "initialize mapbox",
    () => {
      // for some reason, we were already initialized, but some info became unavailable. This should not happen
      if (!allAvailable && mapElement.current != null) {
        return EffectResult.EarlyOut;
      }

      if (!mapElement.current) {
        return EffectResult.EarlyOut;
      }

      const sceneId = serializeMapId(mapId);

      mapElement.current.innerHTML = ""; // erase all child nodes. This is necessary for hot reloading

      // this is how mapbox gets the values internally, see `_containerDimensions`
      // setting these values before mapbox fully initializes reduces incorrect data retrieval
      const renderSizeAt1x = {
        width: mapElement.current.clientWidth || 400,
        height: mapElement.current.clientHeight || 300,
      };

      // mapbox does not support changing the container element after initialization. (We would like to attach to `mapElement.current`.)
      // Thus, we create an intermediate element that serves as a container.
      const container: HTMLElement = document.createElement("div");
      mapElement.current.appendChild(container);

      // TODO: grab all necessary init data first instead of filling it in with garbage
      const visualization: ScenePublicApi = compositor.getScene(sceneId, () => {
        const projection: {
          name:
            | "albers"
            | "equalEarth"
            | "equirectangular"
            | "lambertConformalConic"
            | "mercator"
            | "naturalEarth"
            | "winkelTripel"
            | "globe";
          center?: [number, number];
          parallels?: [number, number];
        } = { name: "mercator" };

        const mapboxOptions: mapboxgl.MapboxOptions & { showCollisionBoxes?: boolean } = {
          container,
          fadeDuration: 0,
          // Keep more recently visited basemap tiles around during short pan gestures.
          minTileCacheSize: MAPBOX_MIN_TILE_CACHE_SIZE,
          pitchWithRotate: true,
          showCollisionBoxes: false,
          style: emptyStyle,
          interactive: true,
          attributionControl: false,
          dragRotate: false, //TODO limit userinteraction for now to sync split view. Currently bearing and pitch are not synced
          projection,
          // Replace png sprite atlas to webp, as we want to use more modern image format
          transformRequest: ((url: string, resourceType?: ResourceType) => ({
            url: resourceType === "SpriteImage" ? url.replace("png", "webp") : url,
          })) as mapboxgl.RequestTransformFunction,
        };

        const map = compositor.getMapboxMapFromPool(sceneId, () => mapboxOptions);

        // For some reason official typing for mapbox (@types/mapbox-gl) doesn't
        // have typed projection. One of solutions is to extend typing by
        // adding missing types, but that's not very good approach, as there
        // must be typing library that works
        // TODO: Fix type is missing

        // Mapbox pool returns any unused mapbox instance, without taking projection in account.
        // It means - if we cache mapbox instance with globe projection, and try to reuse it where
        // mercator projection is expected, we will see "globe" projected map.
        // To fix that we need to make sure, that mapbox has right projection before rendering it.
        // This solution may not work, if we will decide to use projection transitions to make
        // UX more appealing (in that case refactoring of ObjectPool will be only option)

        const projectionName =
          typeof mapboxOptions.projection === "string" ? mapboxOptions.projection : mapboxOptions.projection?.name;
        if (projectionName && map.getProjection().name !== projectionName) {
          // TODO: Fix type is missing
          map.setProjection(mapboxOptions.projection);
        }
        return {
          id: sceneId,
          displayTime: displayTime,
          nextTime: nextTime,
          renderSize: renderSizeAt1x,
          map,
          isAnimation: timeState.matches(TimeStates.ANIMATION),
          lodBias: cartographicMap?.lod_bias || 1,
          timeOffsetMinutes: cartographicMap?.time_offset_mins || 0,
        };
      });

      const map = visualization.getMapboxMap();
      // reference comparing
      if (container !== map.getContainer()) {
        mapElement.current.innerHTML = ""; // erase all child nodes. This is necessary for hot reloading
        // add the old container to mapElement
        mapElement.current.appendChild(map.getContainer());
      }

      // TODO: the map initializes to the wrong size since the `container` is attached to the DOM after the mapbox.Map is constructed.
      // However, it's weird that we have to call resize ourselves.
      // Should also not be necessary since we set the size above
      map.resize();

      if (viewport != null) {
        visualization.syncMapboxToViewport(viewport);
      }

      // map was restored from a cached version, run onMapLoad explicitly since "load" already fired
      // TODO: this reorders operations, which should be avoided.
      if (visualization.isMapboxLoaded()) {
        onMapLoad();
      } else {
        map.on("load", onMapLoad);
      }

      return () => {
        if (visualization) {
          compositor.free(serializeMapId(mapId));

          const map = visualization.getMapboxMap();
          visualization.getPopupMarker()?.remove();
          map.off("load", onMapLoad);
          // Make sure we remove control, when map wrapper gets destroyed
          if (map.hasControl(scale)) {
            map.removeControl(scale);
          }
        }
      };

      function onMapLoad() {
        if (visualization === null) {
          return;
        }

        if (!map.hasControl(scale)) {
          map.addControl(scale, "bottom-right");
        }
        // [DEBUG-TILE-ALIGNMENT] map.showTileBoundaries = true;
        // [DEBUG-OVERDRAW] // @ts-expect-error
        // [DEBUG-OVERDRAW] map.showOverdrawInspector = true;
        trace("mapbox finished initializing", visualization);
        setScene(visualization);
      }
    },
    // This handler should only be run once on component mount to initialize the map. Any dependencies are thus ignored
    // and should only be used to enhance the initial state of the map.
    [mapId, mapElement, allAvailable],
  );

  return (
    // Added "id" so image export could find map container
    <div
      className="map-container overlay overlay__layer"
      id={generateIdForSnapshot(SnapshotToolHtmlIds.Map, mapId.map)}
      data-testid="map-tool"
    >
      <div
        className="grid grid--cover"
        ref={mapElement}
        style={isLayoutArrangementActive ? { pointerEvents: "none" } : {}}
      />
    </div>
  );
}
